import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { get, post, patch, errMsg, inr, dt, label, download, PRODUCT_OPTIONS } from "@/lib/api";
import { useAuth } from "@/context/Auth";
import { PageHeader, Card, StatusBadge, Btn, Field, Table, Pager, Loading, useFetch } from "@/components/kit";

const STAGES = ["LEAD_CREATED", "ELIGIBILITY", "KYC_INITIATED", "KYC_COMPLETED", "CREDIT_CHECK", "LENDER_SUBMISSION", "OFFER_RECEIVED", "CUSTOMER_ACCEPTED", "SANCTION", "DISBURSEMENT", "DISBURSED", "CLOSED", "REJECTED"];

export function Applications() {
  const nav = useNavigate();
  const { can } = useAuth();
  const [f, setF] = useState({ q: "", stage: "", product: "" });
  const [page, setPage] = useState(1);
  const params = { ...Object.fromEntries(Object.entries(f).filter(([, v]) => v)), page };
  const { data } = useFetch(() => get("/applications", params), [JSON.stringify(params)]);
  return (
    <div className="space-y-4">
      <PageHeader title="Applications" subtitle="One customer → one application → multiple lenders.">
        {can("report.export") && <Btn variant="outline" onClick={() => download("/export/applications", "applications.csv")}><Download className="h-4 w-4" />Export CSV</Btn>}
        {can("application.create") && <Btn data-testid="new-application-btn" onClick={() => nav("/crm/applications/new")}>New application</Btn>}
      </PageHeader>
      <div className="flex flex-wrap gap-2">
        <input data-testid="app-search-input" className="fo-input h-9 max-w-xs" placeholder="Search app ID, name, mobile" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} />
        <select data-testid="app-stage-filter" className="fo-input h-9 w-auto" value={f.stage} onChange={(e) => setF({ ...f, stage: e.target.value })}><option value="">All stages</option>{STAGES.map((s) => <option key={s} value={s}>{label(s)}</option>)}</select>
        <select className="fo-input h-9 w-auto" value={f.product} onChange={(e) => setF({ ...f, product: e.target.value })}><option value="">All products</option>{PRODUCT_OPTIONS.map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
      </div>
      {!data ? <Loading /> : <>
        <Table testid="applications-table" rows={data.items} onRow={(a) => nav(`/crm/applications/${a.id}`)} cols={[
          { k: "app_code", h: "App ID", r: (a) => <span className="font-mono text-xs">{a.app_code}</span> }, { k: "customer_name", h: "Customer" }, { k: "product", h: "Product" },
          { k: "loan_amount", h: "Amount", r: (a) => inr(a.loan_amount) }, { k: "stage", h: "Stage", r: (a) => <StatusBadge value={a.stage} /> },
          { k: "kyc_status", h: "KYC", r: (a) => <StatusBadge value={a.kyc_status} /> }, { k: "selected_lender_name", h: "Lender" }, { k: "source", h: "Source", r: (a) => label(a.source) },
          { k: "created_at", h: "Created", r: (a) => dt(a.created_at) },
        ]} />
        <Pager page={page} size={data.size} total={data.total} setPage={setPage} />
      </>}
    </div>
  );
}

export function Customers() {
  const { can } = useAuth();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const { data } = useFetch(() => get("/customers", { q: q || undefined, page }), [q, page]);
  const act = async (c, kind) => {
    try {
      if (kind === "export") { const r = await get(`/customers/${c.id}/export`); const b = new Blob([JSON.stringify(r, null, 2)], { type: "application/json" }); const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = `${c.customer_code}.json`; a.click(); }
      else { if (!window.confirm("Deactivate this customer?")) return; await post(`/customers/${c.id}/deactivate`); toast.success("Deactivated"); }
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4">
      <PageHeader title="Customers" subtitle="PAN stored encrypted; only masked values are shown." />
      <input data-testid="customer-search-input" className="fo-input h-9 max-w-xs" placeholder="Search name, mobile, PAN, ID" value={q} onChange={(e) => setQ(e.target.value)} />
      {!data ? <Loading /> : <>
        <Table testid="customers-table" rows={data.items} cols={[
          { k: "customer_code", h: "Customer ID", r: (c) => <span className="font-mono text-xs">{c.customer_code}</span> }, { k: "full_name", h: "Name" }, { k: "mobile", h: "Mobile" },
          { k: "email", h: "Email" }, { k: "pan_masked", h: "PAN" }, { k: "city", h: "City" }, { k: "active", h: "Status", r: (c) => (c.active === false ? "Inactive" : "Active") },
          { k: "a", h: "", r: (c) => can("admin.manage") && <div className="flex gap-2"><button onClick={() => act(c, "export")} className="text-xs font-semibold text-blue-800">Export data</button><button onClick={() => act(c, "deactivate")} className="text-xs font-semibold text-rose-600">Deactivate</button></div> },
        ]} />
        <Pager page={page} size={data.size} total={data.total} setPage={setPage} />
      </>}
    </div>
  );
}

export function Tasks() {
  const { can } = useAuth();
  const [mine, setMine] = useState(true);
  const { data, reload } = useFetch(() => get("/tasks", { mine }), [mine]);
  const users = useFetch(() => (can("lead.assign") ? get("/users") : Promise.resolve({ items: [] })), []);
  const [f, setF] = useState({ type: "CALL_CUSTOMER", priority: "MEDIUM" });
  const save = async () => { try { await post("/tasks", f); toast.success("Task created"); setF({ type: "CALL_CUSTOMER", priority: "MEDIUM" }); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const setStatus = async (t, status) => { await patch(`/tasks/${t.id}`, { status }); reload(); };
  const P = { form: f, setForm: setF };
  return (
    <div className="space-y-6">
      <PageHeader title="Tasks"><label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} />Only mine</label></PageHeader>
      <Card title="New task">
        <div className="grid gap-3 md:grid-cols-5">
          <Field label="Title" name="title" {...P} />
          <Field label="Type" name="type" options={["CALL_CUSTOMER", "VERIFY_DOCUMENT", "COMPLETE_KYC", "FOLLOW_UP", "SUBMIT_LENDER", "CHECK_SANCTION", "DISBURSEMENT_FOLLOW_UP"]} {...P} />
          <Field label="Priority" name="priority" options={["LOW", "MEDIUM", "HIGH"]} {...P} />
          <Field label="Due" name="due_date" type="date" {...P} />
          {users.data?.items?.length > 0 ? <Field label="Assign to" name="assigned_to" options={users.data.items.map((u) => [u.id, u.name])} {...P} /> : <div />}
          <Field label="Remarks" name="remarks" className="md:col-span-4" {...P} />
        </div>
        <Btn className="mt-3" data-testid="create-task-btn" onClick={save}>Create task</Btn>
      </Card>
      <Table testid="tasks-table" rows={data || []} cols={[{ k: "title", h: "Task" }, { k: "type", h: "Type", r: (t) => label(t.type) }, { k: "priority", h: "Priority", r: (t) => <StatusBadge value={t.priority} /> },
        { k: "assigned_to_name", h: "Assigned" }, { k: "due_date", h: "Due" }, { k: "status", h: "Status", r: (t) => <StatusBadge value={t.status} /> },
        { k: "a", h: "", r: (t) => t.status !== "DONE" && <button data-testid={`task-done-${t.id}`} onClick={() => setStatus(t, "DONE")} className="text-xs font-semibold text-emerald-700">Mark done</button> }]} />
    </div>
  );
}

export function Tickets() {
  const { data, reload } = useFetch(() => get("/tickets"), []);
  const resolve = async (t) => { const r = window.prompt("Resolution note"); if (!r) return; try { await patch(`/tickets/${t.id}`, { status: "RESOLVED", resolution: r }); reload(); } catch (e) { toast.error(errMsg(e)); } };
  if (!data) return <Loading />;
  return (
    <div className="space-y-4">
      <PageHeader title="Grievance & support tickets" />
      <Table testid="tickets-table" rows={data} cols={[{ k: "ticket_code", h: "Ticket" }, { k: "customer_name", h: "Raised by" }, { k: "category", h: "Category", r: (t) => label(t.category) },
        { k: "priority", h: "Priority", r: (t) => <StatusBadge value={t.priority} /> }, { k: "subject", h: "Subject" }, { k: "assigned_team", h: "Team" }, { k: "sla_due", h: "SLA due", r: (t) => dt(t.sla_due) },
        { k: "status", h: "Status", r: (t) => <StatusBadge value={t.status} /> }, { k: "a", h: "", r: (t) => t.status !== "RESOLVED" && <button onClick={() => resolve(t)} className="text-xs font-semibold text-emerald-700">Resolve</button> }]} />
    </div>
  );
}
